import {
  CreateWorkspaceRequest,
  WorkspaceResult,
  WorkspaceService,
} from '@/api/';
import { action, atom, onSet } from 'nanostores';

/**
 * The current selected workspace
 */
export const $workspace = atom<WorkspaceResult | undefined>();

/**
 * The workspaces for the current selected user and org.
 */
export const $workspaces = atom<WorkspaceResult[]>([]);

/**
 * Main function to load workspacs
 */
const loadWorkspaces = async (orgId: string) => {
  if (orgId) {
    console.log('Retrieving workspaces...');

    await ensureOrganization(orgId);

    var workspaces = await WorkspaceService.getWorkspaces(orgId);


    $workspaces.set(workspaces);

    // set the workspace ids in local storage since we are gonna use it later
    // for drag and drop, these workspace ids will act as order of the workspaces
    const existingWorkspaceOrders = JSON.parse(
      localStorage.getItem('workspaces_orders') ?? '{}',
    );

    if (!existingWorkspaceOrders[orgId]) {
      existingWorkspaceOrders[orgId] = workspaces.map(w => w.workspace.id);
      localStorage.setItem(
        'workspaces_orders',
        JSON.stringify(existingWorkspaceOrders),
      );
    }

  }
};

/**
 * Reload workspaces on org change
 */
$org.subscribe(async org => {
  $workspaces.set([]);

  await loadWorkspaces(org?.id ?? '');
});

/**
 * When the workspace changes, we subscribe for notifications.
 */
onSet($workspace, async ws => {
  if (!ws.newValue) {
    return;
  }

  if (ws.newValue.workspace.id == $workspace.get()?.workspace.id) {
    return;
  }

  await signalRService.joinWorkspaceGroup(ws.newValue.workspace.id);
});

/**
 * Watch for route changes; if we haven't loaded the orgs, we try to load them.
 */
route.subscribe(async name => {
  if (name !== 'workspaces' || ($workspaces.value?.length ?? 0) > 0) {
    return;
  }

  const params = $router.get()?.params as Record<'organizationId', string>;

  await loadWorkspaces(params.organizationId);
});

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
export const createWorkspace = action(
  $workspace,
  'createWorkspace',
  async (store, name: string, description?: string) => {
    const params = $router.get()?.params as Record<'organizationId', string>;

    const request: CreateWorkspaceRequest = {
      name,
      description,
      orgId: params.organizationId,
    };

    const workspace = await WorkspaceService.createWorkspace(request);

    const result: WorkspaceResult = {
      workspace,
      invites: 0,
      collaborators: [],
      projects: [],
    };

    $workspaces.set([result, ...$workspaces.get()]);

    $workspace.set(result);
  },
);

/**
 * Delete's the selected workspace.
 */
export const deleteWorkspace = action(
  $workspace,
  'deleteWorkspace',
  async (store, workspaceId: string) => {
    await WorkspaceService.deleteWorkspace(workspaceId);

    $workspace.set(undefined);

    // Splice out the project
    const workspaces = $workspaces.get();

    const index = workspaces.findIndex(w => w.workspace.id === workspaceId);

    if (index > -1) {
      workspaces.splice(index, 1);

      $workspaces.set(workspaces);
    }
  },
);

/**
 * Ensure that the workspace is loaded and selected.
 */
export const ensureWorkspace = action(
  $workspace,
  'ensureWorkspace',
  async (store, workspaceId: string) => {
    let ws: WorkspaceResult | undefined;

    const params = $router.get()?.params as Record<'organizationId', string>;

    if ($workspaces.get().length === 0) {
      const workspaces = await WorkspaceService.getWorkspaces(
        params.organizationId ?? '',
      );

      $workspaces.set(workspaces);

      ws = workspaces.find(w => w.workspace.id === workspaceId);
    } else {
      ws = $workspaces.get().find(w => w.workspace.id === workspaceId);
    }

    $workspace.set(ws);
  },
);
